import { Injectable, ErrorHandler, NgZone } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { environment } from '@sp-environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { PermissionsService, UserService } from '@sp-core-services';
import { combineLatest, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type { User } from '@sentry/browser';
import { ServerErrorModel } from '@sp-core/models/app-models/server-error.model';
import { buildServerErrorReport } from './server-error-report';

const IGNORED_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

// Errors that are plain objects rather than Error instances (ServerErrorModel is the common
// case) reach Sentry twice: once raw, captured by the SDK's own browser integrations, and once
// through SentryService.handleError, where extractError turns them into a readable message.
// The raw copy carries neither a message nor a usable stack — Sentry can only list the object's
// keys — and Dedupe cannot collapse the two because their values differ. Dropping it here keeps
// one readable issue per error instead of two, one of them useless.
const UNRESOLVED_OBJECT_ERROR = 'Object captured as exception with keys';

Sentry.init({
  dsn: environment.dsn,
  environment: environment.env,
  integrations: [
    Sentry.browserApiErrorsIntegration({
      XMLHttpRequest: false,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],

  // Session Replay in buffer mode: recording runs continuously into an in-memory ring buffer
  // (the last 60 seconds) and is uploaded only once an error has been sent successfully.
  // Without an error no recording ever reaches Sentry, which is expected rather than broken.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: environment.replayEnabled ? 1.0 : 0,

  // disabling Sentry for local development: https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-before-send
  beforeSend(event: Sentry.ErrorEvent): PromiseLike<Sentry.ErrorEvent | null> | Sentry.ErrorEvent | null {
    if (!environment.production) {
      return null;
    }

    const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
    if (IGNORED_ERRORS.some((ignored) => errorMessage.includes(ignored))) {
      return null;
    }

    if (errorMessage.startsWith(UNRESOLVED_OBJECT_ERROR)) {
      return null;
    }

    return event;
  },
});

@Injectable({ providedIn: 'root' })
export class SentryService implements ErrorHandler {
  constructor(
    private ngZone: NgZone,
    private userService: UserService,
    private permissionsService: PermissionsService,
  ) {
    this.listenUserChanges();
  }

  /**
   * Method called for every value captured through the ErrorHandler
   */
  public handleError(error: any): void {
    // processed chunk Failed Message
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (chunkFailedMessage.test(error?.message)) {
      window.location.reload();
      return;
    }

    if (error instanceof SyntaxError) {
      window.location.reload();
      return;
    }

    const errorMessage = error?.message || error?.toString() || '';
    if (IGNORED_ERRORS.some((ignored) => errorMessage.includes(ignored))) {
      return; // Silently ignore
    }

    // An API error the code did not handle: report it per status and endpoint, so each problem
    // gets its own issue instead of all of them sharing one untitled "Error:" issue.
    const serverError = this.unwrapZoneError(error);
    if (serverError instanceof ServerErrorModel) {
      const { message, ...context } = buildServerErrorReport(serverError);
      const messageEventId = this.ngZone.runOutsideAngular(() => Sentry.captureMessage(message, context));
      if (!environment.production) {
        console.error(message, serverError);
        Sentry.showReportDialog({ eventId: messageEventId });
      }
      return;
    }

    const extractedError = this.extractError(error) || 'Handled unknown error';

    // Capture handled exception and send it to Sentry.
    const eventId = this.ngZone.runOutsideAngular(() => Sentry.captureException(extractedError));

    // When in development mode, log the error to console for immediate feedback.
    if (!environment.production) {
      console.error(extractedError);
    }

    // When in development mode, show user dialog to provide details on what happened.
    if (!environment.production) {
      Sentry.showReportDialog({ eventId });
    }
  }

  /**
   * Default implementation of error extraction that handles default error wrapping, HTTP responses, ErrorEvent and few other known cases.
   */
  private extractError(errorCandidate: unknown): unknown {
    const error: any = this.unwrapZoneError(errorCandidate);

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // Handle API Server Error
      if (error?.error?.error && error?.status) {
        return `Server returned code ${error.status} with body "${JSON.stringify(error?.error?.error)}"`;
      }

      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent && error.error.message) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Nothing was extracted, fallback to default error message.
    return null;
  }

  /**
   * Try to unwrap zone.js error.
   * https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
   */
  private unwrapZoneError(error: unknown): unknown {
    if (error && (error as { ngOriginalError: Error }).ngOriginalError) {
      return (error as { ngOriginalError: Error }).ngOriginalError;
    }
    return error;
  }

  private listenUserChanges() {
    combineLatest([this.userService.user$, this.permissionsService.role$])
      .pipe(catchError(() => of([null, null])))
      .subscribe(([user, role]) => {
        this.clearUser();
        if (user) {
          this.setUser(user, role);
        }
      });
  }

  private setUser(user, role): void {
    const sentryUser: User = {
      id: user?.id,
      email: user?.email,
      username: `${user?.firstName} ${user?.lastName}`,
      role: role?.name || 'none',
    };
    this.ngZone.runOutsideAngular(() => Sentry.setUser(sentryUser));
  }

  private clearUser(): void {
    this.ngZone.runOutsideAngular(() => Sentry.setUser(null));
  }
}
